import { AgGridReact } from '@mint-lib/ag-grid/react';
import { useMemo, type FC } from 'react';

import {
  CellStyleModule,
  ClientSideRowModelModule,
  ColDef,
  ColumnApiModule,
  ColumnMenuModule,
  ColumnsToolPanelModule,
  ContextMenuModule,
  CustomEditorModule,
  CustomFilterModule,
  LicenseManager,
  ModuleRegistry,
  MultiFilterModule,
  NumberFilterModule,
  RenderApiModule,
  RowApiModule,
  RowAutoHeightModule,
  SetFilterModule,
  SideBarModule,
  TextFilterModule,
  ValidationModule,
  type GetRowIdParams,
  type OverlaySelectorFunc,
} from '@mint-lib/ag-grid/enterprise';
import { createDefaultColumn } from '@mint-lib/ag-grid/factory';
import { useTranslation } from '@mint-lib/i18n';
import { useIsAllowedPermission } from '@mint-lib/profile-helpers';
import { ApprovalFlows } from '@mint-sdk/api-approval-flows';

import NoResult from '../../components/NoResult.jsx';
import {
  FlowTypesEnum,
  getMapChipMetaByFlowStatus,
  getMapLabelByFlowType,
  READONLY_FLOW_STATUSES,
} from '../../constants/singleFlow.js';
import useGridContextMenu from '../../hooks/useGridContextMenu.jsx';
import type { ApprovalTableItemData } from '../../types/approversTable.js';
import ActionCell from './components/ActionCell.jsx';
import ApproverCell from './components/ApproverCell.jsx';
import DateCell from './components/DateCell.jsx';
import DateTimeCell from './components/DateTimeCell.jsx';
import EntitySetFilter from './components/EntitySetFilter.jsx';
import NameCell from './components/NameCell.jsx';
import RequesterCell from './components/RequesterCell.jsx';
import StatusCell from './components/StatusCell.jsx';
import StatusCellEditor from './components/StatusCellEditor.jsx';
import DateRangeFilter from './filters/DateRangeFilter.jsx';

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  RowApiModule,
  ColumnApiModule,
  RenderApiModule,
  ValidationModule,
  ColumnMenuModule,
  SetFilterModule,
  CellStyleModule,
  SideBarModule,
  ColumnsToolPanelModule,
  ContextMenuModule,
  MultiFilterModule,
  TextFilterModule,
  NumberFilterModule,
  CustomFilterModule,
  CustomEditorModule,
  RowAutoHeightModule,
]);
LicenseManager.setLicenseKey(import.meta.env.VITE_AG_GRID_LICENSE_KEY);

const overlayComponentSelector: OverlaySelectorFunc<ApprovalTableItemData> = (
  params,
) => {
  if (params.overlayType === 'noMatchingRows') {
    return {
      component: NoResult,
    };
  }

  if (params.overlayType === 'noRows') {
    return {
      component: () => null,
    };
  }

  return undefined;
};

const getRowId = (params: GetRowIdParams<ApprovalTableItemData>) =>
  params.data.id;

type Props = {
  rowData: ApprovalTableItemData[];
};

const ApproversTable: FC<Props> = ({ rowData }) => {
  const { t } = useTranslation('@myn/approval_v2');
  const { getContextMenuItems, getMainMenuItems } = useGridContextMenu();
  const isReadOnly = !useIsAllowedPermission('approvalFlowFeature:modify');

  const columnDefs = useMemo<ColDef<ApprovalTableItemData>[]>(
    () => [
      {
        field: 'name',
        headerName: t('Approval flow name'),
        lockVisible: true,
        lockPosition: 'left',
        lockPinned: true,
        pinned: 'left',
        filter: 'agSetColumnFilter',
        cellRenderer: NameCell,
      },
      {
        field: 'flowType',
        headerName: t('Flow type'),
        filter: false,
        minWidth: 120,
        valueFormatter: (params) =>
          params.value === FlowTypesEnum.enum.flight
            ? getMapLabelByFlowType(t)[FlowTypesEnum.enum.flight]
            : params.value,
      },
      {
        field: 'status',
        headerName: t('Status'),
        filter: 'agSetColumnFilter',
        filterParams: {
          valueFormatter: ({ value }: { value: ApprovalFlows.FlowStatus }) =>
            getMapChipMetaByFlowStatus(t)[value].label,
        },
        cellRenderer: StatusCell,
        cellEditor: StatusCellEditor,
        editable: ({ data }) => {
          if (isReadOnly || !data?.status) {
            return false;
          }

          return !READONLY_FLOW_STATUSES.includes(data.status);
        },
      },
      {
        field: 'requesters',
        headerName: t('Requester'),
        filter: { component: EntitySetFilter },
        filterParams: { field: 'requesters' },
        cellRenderer: RequesterCell,
        autoHeight: true,
        initialFlex: 2,
        minWidth: 200,
        sortable: false,
      },
      {
        field: 'approvers',
        headerName: t('Approver'),
        filter: { component: EntitySetFilter },
        filterParams: { field: 'approvers' },
        cellRenderer: ApproverCell,
        autoHeight: true,
        initialFlex: 2,
        minWidth: 200,
        sortable: false,
      },
      {
        field: 'dateFrom',
        headerName: t('Start date'),
        cellRenderer: DateCell,
        filter: {
          component: DateRangeFilter,
        },
      },
      {
        field: 'dateTo',
        headerName: t('End date'),
        cellRenderer: DateCell,
        filter: {
          component: DateRangeFilter,
        },
      },
      {
        field: 'lastEditedBy',
        headerName: t('Last edited by'),
      },
      {
        field: 'lastEditedAt',
        headerName: t('Last edit date'),
        cellRenderer: DateTimeCell,
        filter: {
          component: DateRangeFilter,
        },
      },
      {
        field: 'createdBy',
        headerName: t('Created by'),
      },
      {
        field: 'createdDate',
        headerName: t('Created date'),
        cellRenderer: DateTimeCell,
        filter: {
          component: DateRangeFilter,
        },
      },
      {
        colId: 'actionCell',
        headerName: '',
        lockPosition: 'right',
        lockPinned: true,
        pinned: 'right',
        minWidth: 40,
        width: 40,
        resizable: false,
        cellClass: 'p-0!',
        sortable: false,
        filter: false,
        suppressMovable: true,
        suppressFillHandle: true,
        suppressPaste: true,
        suppressHeaderMenuButton: true,
        suppressHeaderContextMenu: true,
        suppressColumnsToolPanel: true,
        cellRenderer: ActionCell,
      },
    ],
    [t, isReadOnly],
  );

  const defaultColDef = useMemo<ColDef<ApprovalTableItemData>>(
    () =>
      createDefaultColumn({
        initialFlex: 1,
        minWidth: 140,
        sortable: true,
        filter: true,
      }),
    [],
  );

  return (
    <div className="grow rounded-lg border border-ui-tertiary shadow-md">
      <AgGridReact
        rowData={rowData}
        loading={false}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        maintainColumnOrder
        overlayComponentSelector={overlayComponentSelector}
        getRowId={getRowId}
        getContextMenuItems={getContextMenuItems}
        getMainMenuItems={getMainMenuItems}
        popupParent={document.body}
      />
    </div>
  );
};

export default ApproversTable;
